import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { getTerritoryVisits } from '../../API/apiService';
import {
  buildProtectedImageUrl,
  fetchWithAuth,
  getApiV1BaseUrl
} from '../../utils/secureMedia';
import './style.scss';

const SESSION_LABELS = {
  sesion_docentes: 'Sesión Docentes',
  sesion_estudiantes: 'Sesión Estudiantes',
  sesion_comunidad: 'Sesión Comunidad',
  evento_docentes: 'Evento Docentes',
  evento_estudiantes: 'Evento Estudiantes',
  evento_comunidad: 'Evento Comunidad'
};

const ANSWER_LABELS = {
  planned_execution: 'Lo ejecutado correspondió con lo planeado',
  objectives: 'Orientación acorde con los objetivos',
  support_materials: 'Materiales de apoyo adecuados',
  duration: 'Duración adecuada para cumplir el objetivo',
  critical_thinking: 'Pensamiento crítico',
  affiliation: 'Afiliación',
  ethical_praxis: 'Práxis ética',
  environment_control: 'Control del propio entorno',
  imagination: 'Imaginación'
};

const AXIS_LABELS = {
  territorial_development: 'Desarrollo territorial',
  social_control: 'Control social',
  coexistence_conflict_resolution: 'Convivencia y resolución de conflictos',
  inclusion: 'Inclusión'
};

const EMPTY_FILTERS = {
  sessionType: '',
  groupId: '',
  schoolId: '',
  municipalityId: '',
  mediatorId: '',
  observerId: '',
  dateFrom: '',
  dateTo: ''
};

function uniqueOptions(visits, idKey, labelKey) {
  const options = new Map();
  visits.forEach(visit => {
    const id = String(visit?.[idKey] || '');
    const label = String(visit?.[labelKey] || '').trim();
    if (id && label) options.set(id, label);
  });
  return [...options.entries()]
    .map(([id, label]) => ({ id, label }))
    .sort((left, right) => left.label.localeCompare(right.label, 'es'));
}

function yesNo(value) {
  return value === true ? 'Sí' : value === false ? 'No' : '';
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function resolveDownloadFileName(contentDisposition, fallback) {
  const utf8Match = String(contentDisposition || '').match(/filename\*=UTF-8''([^;]+)/i);
  const simpleMatch = String(contentDisposition || '').match(/filename="?([^";]+)"?/i);
  const rawName = utf8Match?.[1] || simpleMatch?.[1];
  if (!rawName) return fallback;
  try {
    return decodeURIComponent(rawName);
  } catch (_error) {
    return rawName;
  }
}

function AnswerBlock({ title, answers, comments }) {
  return (
    <section className='territory-visit-detail__block'>
      <h3>{title}</h3>
      <div className='territory-visit-detail__answers'>
        {Object.entries(answers || {}).map(([key, value]) => (
          <div key={key}>
            <span>{ANSWER_LABELS[key] || key}</span>
            <strong className={value ? 'answer-yes' : 'answer-no'}>
              {value ? 'Sí' : 'No'}
            </strong>
          </div>
        ))}
      </div>
      {comments && <p className='territory-visit-detail__comments'>{comments}</p>}
    </section>
  );
}

function VisitDetail({ visit, onClose }) {
  if (!visit) return null;
  const photoUrl = visit.photo?.id
    ? buildProtectedImageUrl(visit.photo.id, false)
    : '';

  return (
    <div className='territory-visit-detail' role='dialog' aria-modal='true'>
      <article>
        <button
          type='button'
          className='territory-visit-detail__close'
          aria-label='Cerrar detalle'
          onClick={onClose}>
          <span className='material-symbols-rounded'>close</span>
        </button>
        <h2>Detalle de la visita</h2>
        <div className='territory-visit-detail__summary'>
          <p><strong>Tipo:</strong> {SESSION_LABELS[visit.sessionType]}</p>
          <p><strong>Grupo:</strong> {visit.groupCode}</p>
          <p><strong>Institución:</strong> {visit.schoolName}</p>
          <p><strong>Comuna/Corregimiento:</strong> {visit.municipalityName}</p>
          <p><strong>Fecha:</strong> {visit.sessionDate}</p>
          <p><strong>Horario:</strong> {visit.startTime} – {visit.endTime}</p>
          <p><strong>Mediador:</strong> {visit.mediatorName}</p>
          <p><strong>Observador:</strong> {visit.observerName}</p>
          <p><strong>Cargo:</strong> {visit.observerPosition}</p>
        </div>

        <AnswerBlock
          title='Observación metodología de la actividad'
          answers={visit.methodologyAnswers}
          comments={visit.methodologyComments}
        />
        <AnswerBlock
          title='Capacidades desde el facilitador'
          answers={visit.facilitatorCapacityAnswers}
          comments={visit.facilitatorComments}
        />
        <AnswerBlock
          title='Capacidades desde el grupo participante'
          answers={visit.participantCapacityAnswers}
          comments={visit.participantComments}
        />

        <section className='territory-visit-detail__block'>
          <h3>Ejes problematizadores</h3>
          <ul>
            {(visit.problemAxes || []).map(axis => (
              <li key={axis}>{AXIS_LABELS[axis] || axis}</li>
            ))}
          </ul>
          {visit.problemAxesComments && (
            <p className='territory-visit-detail__comments'>
              {visit.problemAxesComments}
            </p>
          )}
        </section>

        <section className='territory-visit-detail__block'>
          <h3>Registro fotográfico</h3>
          {photoUrl ? (
            <img
              className='territory-visit-detail__photo'
              src={photoUrl}
              alt='Registro fotográfico de la visita'
            />
          ) : (
            <p>Esta visita no tiene fotografía registrada.</p>
          )}
        </section>
      </article>
    </div>
  );
}

export default function TerritoryVisitsList() {
  const [visits, setVisits] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);

  useEffect(() => {
    let active = true;
    getTerritoryVisits().then(data => {
      if (active) setVisits(data);
      if (active) setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  const filterOptions = useMemo(
    () => ({
      groups: uniqueOptions(visits, 'groupId', 'groupCode'),
      schools: uniqueOptions(visits, 'schoolId', 'schoolName'),
      municipalities: uniqueOptions(
        visits,
        'municipalityId',
        'municipalityName'
      ),
      mediators: uniqueOptions(visits, 'mediatorId', 'mediatorName'),
      observers: uniqueOptions(visits, 'observerId', 'observerName')
    }),
    [visits]
  );

  const filteredVisits = useMemo(
    () =>
      visits.filter(visit => {
        if (filters.sessionType && visit.sessionType !== filters.sessionType) return false;
        if (filters.groupId && String(visit.groupId) !== filters.groupId) return false;
        if (filters.schoolId && String(visit.schoolId) !== filters.schoolId) return false;
        if (
          filters.municipalityId &&
          String(visit.municipalityId) !== filters.municipalityId
        ) return false;
        if (filters.mediatorId && String(visit.mediatorId) !== filters.mediatorId) return false;
        if (filters.observerId && String(visit.observerId) !== filters.observerId) return false;
        if (filters.dateFrom && visit.sessionDate < filters.dateFrom) return false;
        if (filters.dateTo && visit.sessionDate > filters.dateTo) return false;
        return true;
      }),
    [filters, visits]
  );

  function updateFilter(field, value) {
    setFilters(current => ({ ...current, [field]: value }));
  }

  function downloadExcel() {
    if (filteredVisits.length === 0) {
      toast('No hay visitas para exportar', { type: 'warning' });
      return;
    }

    const rows = filteredVisits.map(visit => ({
      'ID de visita': visit.id,
      'Tipo de sesión': SESSION_LABELS[visit.sessionType] || visit.sessionType,
      'Código de grupo': visit.groupCode,
      'Institución educativa': visit.schoolName,
      'Comuna/Corregimiento': visit.municipalityName,
      'Fecha de realización': visit.sessionDate,
      'Hora de inicio': visit.startTime,
      'Hora final': visit.endTime,
      'Nombre del mediador': visit.mediatorName,
      'Nombre del observador': visit.observerName,
      'Cargo del observador': visit.observerPosition,
      'Metodología - ejecutado según lo planeado':
        yesNo(visit.methodologyAnswers?.planned_execution),
      'Metodología - orientación según objetivos':
        yesNo(visit.methodologyAnswers?.objectives),
      'Metodología - materiales adecuados':
        yesNo(visit.methodologyAnswers?.support_materials),
      'Metodología - duración adecuada':
        yesNo(visit.methodologyAnswers?.duration),
      'Comentarios de metodología': visit.methodologyComments || '',
      'Facilitador - pensamiento crítico':
        yesNo(visit.facilitatorCapacityAnswers?.critical_thinking),
      'Facilitador - afiliación':
        yesNo(visit.facilitatorCapacityAnswers?.affiliation),
      'Facilitador - práxis ética':
        yesNo(visit.facilitatorCapacityAnswers?.ethical_praxis),
      'Facilitador - control del propio entorno':
        yesNo(visit.facilitatorCapacityAnswers?.environment_control),
      'Facilitador - imaginación':
        yesNo(visit.facilitatorCapacityAnswers?.imagination),
      'Comentarios de capacidades del facilitador':
        visit.facilitatorComments || '',
      'Grupo participante - pensamiento crítico':
        yesNo(visit.participantCapacityAnswers?.critical_thinking),
      'Grupo participante - afiliación':
        yesNo(visit.participantCapacityAnswers?.affiliation),
      'Grupo participante - práxis ética':
        yesNo(visit.participantCapacityAnswers?.ethical_praxis),
      'Grupo participante - control del propio entorno':
        yesNo(visit.participantCapacityAnswers?.environment_control),
      'Grupo participante - imaginación':
        yesNo(visit.participantCapacityAnswers?.imagination),
      'Comentarios de capacidades del grupo':
        visit.participantComments || '',
      'Ejes problematizadores': (visit.problemAxes || [])
        .map(axis => AXIS_LABELS[axis] || axis)
        .join(', '),
      'Comentarios de ejes problematizadores':
        visit.problemAxesComments || '',
      'Tiene registro fotográfico': visit.photo?.id ? 'Sí' : 'No'
    }));
    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = Object.keys(rows[0]).map(header => ({
      wch: Math.min(55, Math.max(14, header.length + 2))
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visitas');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `visitas_territorio_${date}.xlsx`);
    toast('Excel generado correctamente', { type: 'success' });
  }

  async function downloadPhotos() {
    if (downloadingPhotos) return;
    const visitIds = filteredVisits
      .filter(visit => visit.photo?.id)
      .map(visit => Number(visit.id))
      .filter(id => Number.isInteger(id) && id > 0);
    if (visitIds.length === 0) {
      toast('Las visitas seleccionadas no tienen fotografías', {
        type: 'warning'
      });
      return;
    }

    setDownloadingPhotos(true);
    try {
      const response = await fetchWithAuth(
        `${getApiV1BaseUrl()}/territory-visits/photos/archive`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ visitIds })
        }
      );
      if (!response.ok) throw new Error('VISIT_PHOTOS_DOWNLOAD_FAILED');
      const blob = await response.blob();
      const fileName = resolveDownloadFileName(
        response.headers.get('content-disposition'),
        `evidencias_visitas_${new Date().toISOString().slice(0, 10)}.zip`
      );
      downloadBlob(blob, fileName);
      toast('Descarga de fotografías iniciada', { type: 'success' });
    } catch (error) {
      console.error(error);
      toast('No fue posible descargar las fotografías', { type: 'error' });
    } finally {
      setDownloadingPhotos(false);
    }
  }

  function renderSelect(field, placeholder, options) {
    return (
      <select
        value={filters[field]}
        onChange={event => updateFilter(field, event.target.value)}>
        <option value=''>{placeholder}</option>
        {options.map(option => (
          <option key={option.id} value={option.id}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <section className='territory-visits-list'>
      <VisitDetail visit={selectedVisit} onClose={() => setSelectedVisit(null)} />

      <section className='territory-visits-filters'>
        <header>
          <h1>Filtros de búsqueda de visitas</h1>
          <button type='button' onClick={() => setFilters(EMPTY_FILTERS)}>
            Limpiar filtros
          </button>
        </header>
        <div>
          <select
            value={filters.sessionType}
            onChange={event => updateFilter('sessionType', event.target.value)}>
            <option value=''>Todos los tipos de sesión</option>
            {Object.entries(SESSION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {renderSelect('groupId', 'Todos los grupos', filterOptions.groups)}
          {renderSelect(
            'schoolId',
            'Todas las instituciones',
            filterOptions.schools
          )}
          {renderSelect(
            'municipalityId',
            'Todas las comunas/corregimientos',
            filterOptions.municipalities
          )}
          {renderSelect(
            'mediatorId',
            'Todos los mediadores',
            filterOptions.mediators
          )}
          {renderSelect(
            'observerId',
            'Todos los observadores',
            filterOptions.observers
          )}
          <label>
            <span>Desde</span>
            <input
              type='date'
              value={filters.dateFrom}
              onChange={event => updateFilter('dateFrom', event.target.value)}
            />
          </label>
          <label>
            <span>Hasta</span>
            <input
              type='date'
              value={filters.dateTo}
              onChange={event => updateFilter('dateTo', event.target.value)}
            />
          </label>
        </div>
      </section>

      <section className='territory-visits-results'>
        <header>
          <h2>Visitas registradas</h2>
          <div className='territory-visits-results__header-actions'>
            <p>Mostrando <strong>{filteredVisits.length}</strong> resultados</p>
            <button type='button' onClick={downloadExcel}>
              <span className='material-symbols-rounded'>table_view</span>
              Descargar Excel
            </button>
            <button
              type='button'
              disabled={downloadingPhotos}
              onClick={downloadPhotos}>
              <span className='material-symbols-rounded'>photo_library</span>
              {downloadingPhotos ? 'Preparando...' : 'Descargar fotografías'}
            </button>
          </div>
        </header>
        <div className='territory-visits-results__content'>
          {loading ? (
            <p className='territory-visits-results__state'>Cargando visitas...</p>
          ) : filteredVisits.length === 0 ? (
            <p className='territory-visits-results__state'>
              No hay visitas que coincidan con los filtros.
            </p>
          ) : (
            <div className='territory-visits-table'>
              <div className='territory-visits-table__row territory-visits-table__row--head'>
                <span>Tipo de sesión</span>
                <span>Grupo</span>
                <span>Institución</span>
                <span>Comuna/Corregimiento</span>
                <span>Fecha y hora</span>
                <span>Mediador</span>
                <span>Observador</span>
                <span>Acciones</span>
              </div>
              {filteredVisits.map(visit => (
                <div className='territory-visits-table__row' key={visit.id}>
                  <span>{SESSION_LABELS[visit.sessionType]}</span>
                  <span>{visit.groupCode}</span>
                  <span>{visit.schoolName}</span>
                  <span>{visit.municipalityName}</span>
                  <span>{visit.sessionDate}<br />{visit.startTime} – {visit.endTime}</span>
                  <span>{visit.mediatorName}</span>
                  <span>{visit.observerName}</span>
                  <span>
                    <button
                      type='button'
                      aria-label='Ver detalle de la visita'
                      title='Ver detalle'
                      onClick={() => setSelectedVisit(visit)}>
                      <span className='material-symbols-rounded'>visibility</span>
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </section>
  );
}
